/**
 *
 * @module sessionManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/session.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';

const AccessTokenProvider = Token.AccessTokenProvider;
const SessionManagerConfiguration = Services.Configuration.SessionManagerConfiguration;
const SessionClient = Services.Session.SessionClient;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class SessionManager
 * @desc Manages the current session and serves as a go
 * between for the public APIs and the SessionClient.
 *
 */
export default class SessionManager {

    /**
     *
     * @param {SDK.Services.Configuration.SessionManagerConfiguration} config
     * @param {SDK.Services.Session.SessionClient} client
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, client, logger = Logging, accessTokenProvider) {

        const errorMessage = {
            ctor: 'SessionManager(config, client, logger, accessTokenProvider)',
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.SessionManagerConfiguration instance`;
            },
            get client() {
                return `${this.ctor} needs SDK.Services.Session.SessionClient instance`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs SDK.Token.AccessTokenProvider instance`;
            }
        };

        instanceStrict(config, SessionManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(client, SessionClient, errorMessage.client, InvalidArgumentException);
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.SessionManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.Session.SessionClient}
         *
         */
        this.client = client;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        this.logger.log(SessionManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @since 3.1.0
     * @desc Gets information about the current session.
     * @returns {Promise<SDK.Services.Session.SessionInfo>}
     *
     */
    getInfo() {

        const { accessToken } = this;

        return this.client.getInfo(accessToken);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.accessTokenProvider.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/session/sessionManager.js